import React, { useMemo } from "react";
import { Plus, Trash2, User, Sparkles } from "lucide-react";
import { Field, DAY_LABELS, DAY_ORDER, slotMinutes, fmtMins } from "./shared";

const Step2Students = ({ students, updateStudent, addStudent, removeStudent, toggleSlot, scheduleSlots, capacity }) => {
  const grouped = useMemo(() => {
    const g = {};
    for (const s of (scheduleSlots || [])) {
      if (!s.day) continue;
      if (!g[s.day]) g[s.day] = [];
      g[s.day].push(s);
    }
    for (const d of Object.keys(g)) {
      g[d].sort((a, b) => (a.startTime || "").localeCompare(b.startTime || ""));
    }
    return g;
  }, [scheduleSlots]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-serif-display text-2xl text-stone-800">2. Élèves et créneaux</h3>
        <p className="text-stone-500 text-sm mt-1">Ajoutez chaque élève et sélectionnez les créneaux souhaités dans le planning hebdomadaire. Le tarif est calculé selon le volume horaire total.</p>
      </div>
      {students.map((st, idx) => {
        const weeklyMins = (st.slotIds || []).reduce((acc, sid) => {
          const slot = (scheduleSlots || []).find((s) => s.id === sid);
          return acc + (slot ? slotMinutes(slot) : 0);
        }, 0);
        return (
          <div key={idx} className="bg-cream rounded-2xl p-4 sm:p-5 border border-blush-light">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <h4 className="font-serif-display text-lg text-stone-800 flex items-center gap-2">
                <User size={18} className="text-deep-rose" /> Élève {idx + 1}
              </h4>
              <div className="flex items-center gap-3">
                {weeklyMins > 0 && (
                  <span className="inline-flex items-center gap-1.5 text-xs bg-blush-light text-deep-rose px-3 py-1 rounded-full" data-testid={`student-weekly-${idx}`}>
                    <Sparkles size={12}/> {fmtMins(weeklyMins)} / sem · {(st.slotIds || []).length} créneau{(st.slotIds || []).length > 1 ? "x" : ""}
                  </span>
                )}
                {students.length > 1 && (
                  <button onClick={() => removeStudent(idx)} className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600" data-testid={`student-remove-${idx}`}><Trash2 size={14}/></button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <Field label="Prénom *" name={`student-${idx}-first`} autoComplete="off" placeholder="Prénom de l'élève" value={st.firstName} onChange={(e) => updateStudent(idx, { firstName: e.target.value })} small required data-testid={`student-first-${idx}`} />
              <Field label="Nom *" name={`student-${idx}-last`} autoComplete="off" placeholder="Nom de famille" value={st.lastName} onChange={(e) => updateStudent(idx, { lastName: e.target.value })} small required data-testid={`student-last-${idx}`} />
              <Field label="Date de naissance *" name={`student-${idx}-birth`} autoComplete="off" type="date" value={st.birthDate} onChange={(e) => updateStudent(idx, { birthDate: e.target.value })} small required data-testid={`student-birth-${idx}`} />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-stone-500 mb-2">Créneaux choisis *</label>
              <div className="space-y-4">
                {DAY_ORDER.filter((d) => grouped[d]?.length).map((dayKey) => (
                  <div key={dayKey}>
                    <div className="text-xs uppercase tracking-widest text-deep-rose font-medium mb-2">{DAY_LABELS[dayKey]}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {grouped[dayKey].map((slot) => {
                        const checked = (st.slotIds || []).includes(slot.id);
                        const cap = capacity[slot.id] || {};
                        const isFull = cap.full;
                        const mins = slotMinutes(slot);
                        return (
                          <label key={slot.id} className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${
                            isFull ? "border-stone-200 bg-stone-50 opacity-60 cursor-not-allowed" :
                            checked ? "border-deep-rose bg-blush-light cursor-pointer" : "border-stone-200 bg-white hover:border-blush cursor-pointer"
                          }`} data-testid={`slot-card-${slot.id}`}>
                            <input type="checkbox" checked={checked} disabled={isFull} onChange={() => toggleSlot(idx, slot.id)} className="mt-1 accent-deep-rose" data-testid={`slot-checkbox-${idx}-${slot.id}`} />
                            <div className="min-w-0 flex-1">
                              <div className="text-[11px] uppercase tracking-widest text-stone-500 font-medium">{slot.startTime} - {slot.endTime} · {fmtMins(mins)}</div>
                              <div className="font-serif-display text-stone-800 flex items-center gap-2 flex-wrap mt-0.5">
                                {slot.title}
                                {slot.price > 0 && <span className="inline-block px-2 py-0.5 rounded-full text-[11px] bg-blush-light text-deep-rose font-medium">{Number(slot.price).toFixed(0)} €/an</span>}
                                {isFull && <span className="inline-block px-2 py-0.5 rounded-full text-[10px] bg-red-100 text-red-700 uppercase tracking-widest">Complet</span>}
                                {!isFull && cap.maxStudents > 0 && cap.remaining !== null && cap.remaining <= 3 && (
                                  <span className="inline-block px-2 py-0.5 rounded-full text-[10px] bg-amber-100 text-amber-700">Plus que {cap.remaining} place{cap.remaining > 1 ? "s" : ""}</span>
                                )}
                              </div>
                              <div className="text-stone-500 text-xs">
                                {slot.category && <span>{slot.category}</span>}
                                {slot.teacherName && <span> · {slot.teacherName}</span>}
                                {slot.room && <span> · {slot.room}</span>}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}
      <button onClick={addStudent} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-blush-light text-deep-rose text-sm" data-testid="add-student-btn">
        <Plus size={16}/> Ajouter un élève
      </button>
    </div>
  );
};

export default Step2Students;
